import { isDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectTargetAccumulatorCurrencyData,
  selectTargetAccumulatorValidation,
} from '@/store/targetAccumulator/selectors.ts';

const LeverageAmount = ({ isSchedule = false }: { isSchedule?: boolean }) => {
  const tileId = useTargetAccumulatorTileId();
  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();
  const isCustom = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.customFields?.includes('LeverageAmount') ?? false,
  );
  const validation = useAppSelector((state) => selectTargetAccumulatorValidation(state, tileId));
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { currency } = useAppSelector((state) => selectTargetAccumulatorCurrencyData(state, tileId));
  const leverageAmount = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.leverageAmount,
  );

  const handleLeverageAmountChanged = useCallback(
    (value: number | undefined) => {
      sgwtWebAnalytics?.trackEvent(
        'Target Accumulator',
        `Leverage amount changed${isSchedule ? ' in schedule' : ''}`,
        isDefined(value) ? String(value) : 'null',
      );

      checkTargetAccumulatorWithTradeCapture({
        leverageAmount: isDefined(value) ? value : null,
      });
    },
    [checkTargetAccumulatorWithTradeCapture, isSchedule, sgwtWebAnalytics],
  );

  return (
    <TargetAccumulatorFormField
      formattedMessageId={`targetAccumulator.form.leverageAmount${isSchedule ? '.short' : ''}`}
    >
      <TradeCaptureValidationWrapper validation={validation?.leverageAmount}>
        <SgAmountInput
          value={leverageAmount === null ? undefined : leverageAmount}
          onChange={handleLeverageAmountChanged}
          className={isCustom ? 'text-info' : undefined}
          data-e2e-is-modified={isCustom}
          unit={currency}
          size="sm"
          allowNegative={false}
          selectAllOnFocus
          data-e2e="leverage-amount"
        />
      </TradeCaptureValidationWrapper>
    </TargetAccumulatorFormField>
  );
};

export default LeverageAmount;
